import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Shield, FileText, CheckCircle, Building2, 
  User, Mail, Phone, Briefcase, DollarSign, Clock, AlertCircle,
  Users, Landmark, Building, Wallet, UserCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    company: '',
    entity_type: 'individual',
    participant_role: 'investor',
    investment_range: '',
    verification_method: 'income',
    self_certification: false,
    broker_dealer_exempt: false,
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const navigate = useNavigate();

  // Entity Types
  const ENTITY_TYPES = [
    { value: 'individual', label: 'Individual (Accredited)', icon: User, desc: 'Natural person meeting SEC accreditation standards' },
    { value: 'trust', label: 'Trust', icon: Shield, desc: 'Revocable or irrevocable trust with qualified assets' },
    { value: 'spv_llc', label: 'SPV / LLC', icon: Building2, desc: 'Special purpose vehicle or limited liability company' },
    { value: 'family_office', label: 'Family Office', icon: Landmark, desc: 'Single or multi-family office structure' },
    { value: 'private_fund', label: 'Private Fund', icon: Wallet, desc: 'Qualified purchaser or accredited investor fund' }
  ];

  // Participant Roles
  const PARTICIPANT_ROLES = [
    { value: 'investor', label: 'Direct Investor', desc: 'Investing on your own behalf' },
    { value: 'private_banker', label: 'Private Banker', desc: 'Acting on behalf of HNWI clients' },
    { value: 'introducer', label: 'Introducer / Referral Agent', desc: 'Introducing qualified investors to the platform' },
    { value: 'family_office_manager', label: 'Family Office Manager', desc: 'Managing investments for family office clients' },
    { value: 'facilitator', label: 'Transaction Facilitator', desc: 'Facilitating transactions for qualified parties' }
  ];

  // Verification methods based on entity type
  const getVerificationMethods = () => {
    if (formData.entity_type === 'individual') {
      return [
        { value: 'income', label: 'Income Verification', desc: 'Annual income > $200K (or $300K joint)' },
        { value: 'net_worth', label: 'Net Worth Verification', desc: 'Net worth > $1M (excluding primary residence)' },
        { value: 'professional', label: 'Professional Certification', desc: 'Licensed broker, investment advisor, CPA, attorney' }
      ];
    }
    return [
      { value: 'entity_assets', label: 'Entity Assets', desc: 'Entity with > $5M in assets' },
      { value: 'beneficial_owners', label: 'Beneficial Owner Accreditation', desc: 'All beneficial owners are accredited' },
      { value: 'qualified_purchaser', label: 'Qualified Purchaser', desc: 'Entity with > $5M in investments' },
      { value: 'institutional', label: 'Institutional Investor', desc: 'Bank, insurance company, registered investment company' }
    ];
  };

  const INVESTMENT_RANGES = [
    '$25K - $100K',
    '$100K - $500K',
    '$500K - $1M',
    '$1M - $5M',
    '$5M - $25M',
    '$25M+'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (!formData.self_certification) {
      toast.error('You must certify your accredited investor/entity status');
      return;
    }

    // For intermediaries, require broker-dealer exemption acknowledgment
    const isIntermediary = ['private_banker', 'introducer', 'family_office_manager', 'facilitator'].includes(formData.participant_role);
    if (isIntermediary && !formData.broker_dealer_exempt) {
      toast.error('Please acknowledge the broker-dealer exemption requirements');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          company: formData.company,
          entity_type: formData.entity_type,
          participant_role: formData.participant_role,
          investment_range: formData.investment_range,
          verification_method: formData.verification_method,
          self_certification: formData.self_certification,
          broker_dealer_exempt: formData.broker_dealer_exempt,
          message: formData.message
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success('Application submitted successfully!');
        setApplicationId(data.application_id);
        setSubmitted(true);
      } else {
        toast.error(data.detail || 'Failed to submit application');
      }
    } catch (error) {
      toast.error('Error submitting application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isIntermediary = ['private_banker', 'introducer', 'family_office_manager', 'facilitator'].includes(formData.participant_role);

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex flex-col" data-testid="application-submitted-page">
        {/* Header */}
        <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#b8860b] flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <div>
                <span className="text-white font-bold text-lg tracking-wide">BETHEREUM</span>
                <p className="text-[#b8860b] text-xs tracking-widest">PRIVATE WEALTH MANAGEMENT</p>
              </div>
            </Link>
          </div>
        </header>

        {/* Success Content */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-lg">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]" data-testid="success-card">
              <CardContent className="py-12 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Application Submitted</h2>
                <p className="text-gray-400 mb-6">
                  Your {ENTITY_TYPES.find(e => e.value === formData.entity_type)?.label} application is being reviewed.
                </p>
                
                <div className="bg-[#1a2d4a] rounded-lg p-4 mb-6 text-left">
                  <p className="text-gray-500 text-xs mb-1">Application ID</p>
                  <p className="text-[#b8860b] font-mono font-bold">{applicationId}</p>
                </div>

                <div className="space-y-4 text-left bg-[#1a2d4a]/50 rounded-lg p-4 mb-6">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#b8860b]" />
                    What's Next?
                  </h3>
                  <ul className="text-gray-400 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-[#b8860b]">1.</span>
                      Our compliance team will review your application within 24-48 hours
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#b8860b]">2.</span>
                      You'll receive an email once your application is approved
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#b8860b]">3.</span>
                      After approval, you can log in with your credentials
                    </li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-[#2a3d5a] text-gray-400 hover:bg-[#1a2d4a]"
                    onClick={() => navigate('/')}
                  >
                    Back to Home
                  </Button>
                  <Button 
                    className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                    onClick={() => navigate('/login')}
                  >
                    Go to Login
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col" data-testid="signup-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#b8860b] flex items-center justify-center">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <div>
              <span className="text-white font-bold text-lg tracking-wide">BETHEREUM</span>
              <p className="text-[#b8860b] text-xs tracking-widest">PRIVATE WEALTH MANAGEMENT</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/compliance" className="text-gray-400 hover:text-white text-sm">Compliance</Link>
            <Link to="/investor-guide" className="text-gray-400 hover:text-white text-sm">Investor Guide</Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6 py-12">
        <div className="w-full max-w-3xl">
          <Button 
            variant="ghost" 
            className="mb-6 text-gray-400 hover:text-white"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Button>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]" data-testid="application-card">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
                <FileText className="w-8 h-8 text-[#b8860b]" />
              </div>
              <CardTitle className="text-2xl text-white">Platform Access Application</CardTitle>
              <CardDescription className="text-gray-400">
                For Accredited Investors, Entities & Qualified Participants
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Important Notice */}
              <div className="mb-6 p-4 bg-[#b8860b]/10 rounded-lg border border-[#b8860b]/30">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#b8860b] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[#b8860b] font-medium text-sm">Reg D 506(b) Private Placement</p>
                    <p className="text-gray-400 text-xs mt-1">
                      This platform is exclusively for accredited investors and qualified entities as defined by SEC regulations. 
                      Private bankers, introducers, and facilitators may participate without broker-dealer registration under applicable exemptions.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Entity Type Selection */}
                <div className="space-y-4">
                  <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                    <Building2 className="w-4 h-4 text-[#b8860b]" />
                    Entity Type *
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {ENTITY_TYPES.map((entity) => {
                      const Icon = entity.icon;
                      return (
                        <button
                          key={entity.value}
                          type="button"
                          onClick={() => setFormData({...formData, entity_type: entity.value, verification_method: entity.value === 'individual' ? 'income' : 'entity_assets'})}
                          className={`p-3 rounded-lg border text-left transition-colors ${
                            formData.entity_type === entity.value
                              ? 'bg-[#b8860b]/20 border-[#b8860b] text-white'
                              : 'bg-[#1a2d4a] border-[#2a3d5a] text-gray-400 hover:border-[#b8860b]/50'
                          }`}
                          data-testid={`entity-${entity.value}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="w-4 h-4" />
                            <span className="font-medium text-sm">{entity.label}</span>
                          </div>
                          <p className="text-xs text-gray-500">{entity.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Participant Role Selection */}
                <div className="space-y-4">
                  <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                    <UserCheck className="w-4 h-4 text-[#b8860b]" />
                    Participant Role *
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {PARTICIPANT_ROLES.map((role) => (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => setFormData({...formData, participant_role: role.value})}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          formData.participant_role === role.value
                            ? 'bg-[#b8860b]/20 border-[#b8860b] text-white'
                            : 'bg-[#1a2d4a] border-[#2a3d5a] text-gray-400 hover:border-[#b8860b]/50'
                        }`}
                        data-testid={`role-${role.value}`}
                      >
                        <p className="font-medium text-sm">{role.label}</p>
                        <p className="text-xs text-gray-500 mt-1">{role.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Personal/Entity Information */}
                <div className="space-y-4">
                  <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-[#b8860b]" />
                    {formData.entity_type === 'individual' ? 'Personal Information' : 'Authorized Representative'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-300">
                        {formData.entity_type === 'individual' ? 'Full Legal Name *' : 'Representative Name *'}
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                        data-testid="name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                        data-testid="email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                        data-testid="phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-gray-300">
                        {formData.entity_type === 'individual' ? 'Company (Optional)' : 'Entity Name *'}
                      </Label>
                      <Input
                        id="company"
                        type="text"
                        placeholder={formData.entity_type === 'individual' ? 'ABC Capital' : 'Entity Legal Name'}
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        required={formData.entity_type !== 'individual'}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                        data-testid="company-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Account Credentials */}
                <div className="space-y-4">
                  <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4 text-[#b8860b]" />
                    Account Credentials
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-gray-300">Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Min. 8 characters"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                        data-testid="password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password *</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                        required
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white placeholder:text-gray-500"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Accreditation Verification */}
                <div className="space-y-4">
                  <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-[#b8860b]" />
                    Accreditation Verification
                  </h3>
                  
                  <div className="space-y-2">
                    <Label className="text-gray-300">Verification Method *</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getVerificationMethods().map((method) => (
                        <button
                          key={method.value}
                          type="button"
                          onClick={() => setFormData({...formData, verification_method: method.value})}
                          className={`p-3 rounded-lg border text-left transition-colors ${
                            formData.verification_method === method.value
                              ? 'bg-[#b8860b]/20 border-[#b8860b] text-white'
                              : 'bg-[#1a2d4a] border-[#2a3d5a] text-gray-400 hover:border-[#b8860b]/50'
                          }`}
                          data-testid={`verification-${method.value}`}
                        >
                          <p className="font-medium text-sm">{method.label}</p>
                          <p className="text-xs text-gray-500 mt-1">{method.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Expected Investment Range</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {INVESTMENT_RANGES.map((range) => (
                        <button
                          key={range}
                          type="button"
                          onClick={() => setFormData({...formData, investment_range: range})}
                          className={`p-2 rounded border text-xs transition-colors ${
                            formData.investment_range === range
                              ? 'bg-[#b8860b] border-[#b8860b] text-white'
                              : 'bg-[#1a2d4a] border-[#2a3d5a] text-gray-400 hover:border-[#b8860b]'
                          }`}
                        >
                          {range}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-300">Additional Information (Optional)</Label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Tell us about your investment goals, any specific questions, or additional context..."
                    rows={3}
                    className="w-full p-3 rounded-lg bg-[#1a2d4a] border border-[#2a3d5a] text-white placeholder:text-gray-500 resize-none"
                    data-testid="message-input"
                  />
                </div>

                {/* Certifications */}
                <div className="space-y-4">
                  {/* Accredited Investor Certification */}
                  <div className="p-4 bg-[#1a2d4a]/50 rounded-lg border border-[#2a3d5a]">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="certification"
                        checked={formData.self_certification}
                        onChange={(e) => setFormData({...formData, self_certification: e.target.checked})}
                        className="mt-1 w-5 h-5 rounded border-[#2a3d5a] bg-[#1a2d4a] text-[#b8860b] focus:ring-[#b8860b]"
                        data-testid="certification-checkbox"
                      />
                      <label htmlFor="certification" className="text-gray-300 text-sm">
                        I certify that I am an <span className="text-[#b8860b] font-medium">accredited investor</span> or 
                        represent an <span className="text-[#b8860b] font-medium">accredited entity</span> as defined by 
                        SEC Rule 501 of Regulation D. I understand that providing false information may result in denial 
                        of access and potential legal consequences. I consent to verification of accreditation status. *
                      </label>
                    </div>
                  </div>

                  {/* Broker-Dealer Exemption (for intermediaries) */}
                  {isIntermediary && (
                    <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="broker_dealer_exempt"
                          checked={formData.broker_dealer_exempt}
                          onChange={(e) => setFormData({...formData, broker_dealer_exempt: e.target.checked})}
                          className="mt-1 w-5 h-5 rounded border-[#2a3d5a] bg-[#1a2d4a] text-blue-500 focus:ring-blue-500"
                          data-testid="broker-dealer-checkbox"
                        />
                        <label htmlFor="broker_dealer_exempt" className="text-gray-300 text-sm">
                          <span className="text-blue-400 font-medium">Broker-Dealer Exemption Acknowledgment:</span> I confirm that my activities 
                          fall within applicable exemptions from broker-dealer registration, including but not limited to: (i) operating as a 
                          finder under SEC no-action letters, (ii) acting as an associated person of a registered entity, (iii) providing 
                          services that do not constitute "effecting transactions" under Section 15(a) of the Exchange Act, or (iv) operating 
                          under state-specific exemptions. I understand my compensation structure and activities must remain within these exemptions. *
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#b8860b] hover:bg-[#9a7209] text-white font-medium py-6"
                  disabled={loading}
                  data-testid="submit-application-btn"
                >
                  {loading ? 'Submitting Application...' : 'Submit Application'}
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm">
                <span className="text-gray-500">Already have an approved account? </span>
                <button 
                  onClick={() => navigate('/login')} 
                  className="text-[#b8860b] hover:underline font-medium"
                  data-testid="login-link"
                >
                  Sign In
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Compliance Notice */}
          <div className="mt-6 p-4 bg-[#0d1d35]/50 rounded-lg border border-[#1a2d4a]">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#b8860b] mt-0.5" />
              <div>
                <p className="text-sm text-gray-400">
                  <span className="text-white font-medium">Reg D 506(b) Compliant:</span> This platform operates 
                  under Regulation D exemptions. All applicants are subject to accreditation verification. Private bankers, 
                  introducers, family office managers, and facilitators may participate under applicable finder and 
                  intermediary exemptions without broker-dealer registration.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#1a2d4a] bg-[#0d1d35] py-4">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs text-gray-500">
            © 2026 Bethereum Private Wealth Management. For accredited investors and qualified entities only.
            <Link to="/compliance" className="text-[#b8860b] hover:underline ml-2">Legal Disclosures</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Signup;
